import {
  ChangeEvent,
  Children,
  ComponentPropsWithoutRef,
  forwardRef,
  KeyboardEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import cn from 'classnames';

import Button from '@/components/common/Button';

type Props = {
  selectedAccountName: string;
  query: string;
  totalCount: number;
  viewAllHref: string;
  placeholder?: string;
  isLoading?: boolean;
  isError?: boolean;
  onClear: () => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSearch: () => void;
} & ComponentPropsWithoutRef<'div'>;

const LNBAccountSearch = forwardRef<HTMLDivElement, Props>(
  (
    {
      selectedAccountName,
      query,
      totalCount,
      viewAllHref,
      placeholder = 'MY BIZ 검색',
      isLoading,
      isError,
      children,
      onClear,
      onChange,
      onSearch,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const layerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const toggleRef = useRef<HTMLElement>(null);
    const renderedChildren = Children.toArray(children).filter(Boolean);

    const onClickToggleButton = () => {
      setIsOpen((prev) => !prev);
    };

    const onClickLayerOutSide = (e: Event) => {
      if (!layerRef.current?.contains(e.target as Node) && !toggleRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const onClickSearchItem = () => {
      setIsOpen(false);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
        onSearch();
      }
    };

    const renderChildren = () => {
      if (isError) {
        return (
          <div className="empty_area">
            <Button className="btn btn_refresh" onClick={onSearch}>
              <span className="ico_wallet ico_refresh1">새로고침</span>
            </Button>
            <p className="txt_empty1">데이터를 불러오지 못했습니다.</p>
          </div>
        );
      }

      if (isLoading) {
        return (
          <div className="empty_area">
            <div className="wrap_loading">
              <span className="loader type_small"></span>
              <span className="screen_out">데이터 로딩중입니다.</span>
            </div>
          </div>
        );
      }

      if (!renderedChildren.length) {
        return (
          <div className="empty_area">
            <p className="txt_empty">검색결과가 없습니다.</p>
          </div>
        );
      }

      return (
        <div className="fake_scroll">
          <ul className="list_account" onClick={onClickSearchItem}>
            {children}
          </ul>
        </div>
      );
    };

    useEffect(() => {
      if (isOpen) {
        document.body.addEventListener('mousedown', onClickLayerOutSide);
        onClear();
      }

      return () => {
        document.body.removeEventListener('mousedown', onClickLayerOutSide);
      };
    }, [isOpen]);

    return (
      <div {...props} ref={ref} className={cn('user_info', { on: isOpen })}>
        <strong ref={toggleRef} className="tit_account" onClick={onClickToggleButton}>
          <span className="txt_g">{selectedAccountName}</span>
          <span className="ico_wallet ico_arr">더보기</span>
        </strong>
        <div className="layer_account" ref={layerRef}>
          <div className="wrap_sch">
            <div className="item_form w_350">
              <div className="opt_comm opt_search">
                <div className="box_tf">
                  <label htmlFor="MY BIZ" className="screen_out">
                    MY BIZ
                  </label>
                  <input
                    ref={inputRef}
                    className="tf_comm"
                    id="MY BIZ"
                    placeholder={placeholder}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    value={query}
                  />
                  <div className="util_g">
                    <Button className="btn_del">
                      <span className="ico_wallet ico_del">삭제</span>
                    </Button>
                    <Button className="btn_search" onClick={onSearch}>
                      <span className="ico_wallet ico_search">검색</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {renderChildren()}
          <a href={viewAllHref} className="link_account" target="_blank" rel="noreferrer">
            전체보기({totalCount})
          </a>
        </div>
      </div>
    );
  }
);

LNBAccountSearch.displayName = 'LNBAccountSearch';

export default LNBAccountSearch;
